// Builds a flat override map from the API `applicationResources` array
// [{ name, value, application }], honoring the legacy priority rules:
//   - custom (application:true) beats default (application:false) for the same name
//   - a webapp-specific 'W-<key>' entry beats the plain '<key>' entry
// The resulting map is keyed by the *lower-cased* plain display key. Legacy $T() always
// matched applicationResources case-insensitively (see the old Globalization.js
// pickTranslation()/textHelper.lowerTrim()), and call sites across the legacy app use
// inconsistent casing for the same key (e.g. $T('orders') vs $T('Orders')) — lower-casing
// here preserves that case-insensitive matching now that flatMessageResolver() and $T() do
// an exact-then-lowercase lookup (see index.js and Globalization.js).

function lowerTrim(str) {
	return (str || '').toString().toLowerCase().trim();
}

export function buildOverlay(resources) {
	if (!Array.isArray(resources) || resources.length === 0) return {};

	// Index by normalized name -> { custom, first } (mirrors legacy buildIndex).
	const index = new Map();
	for (const r of resources) {
		if (!r || !r.value) continue;
		const key = lowerTrim(r.name);
		let bucket = index.get(key);
		if (!bucket) {
			bucket = { custom: null, first: r };
			index.set(key, bucket);
		}
		if (r.application && !bucket.custom) bucket.custom = r;
	}

	const pick = (lookup) => {
		const bucket = index.get(lowerTrim(lookup));
		return bucket ? bucket.custom || bucket.first : null;
	};

	const overlay = {};
	const seenExactCase = new Set();
	for (const r of resources) {
		if (!r || !r.value || !r.name) continue;
		const plain = r.name.startsWith('W-') ? r.name.slice(2) : r.name;
		const key = lowerTrim(plain);
		const chosen = pick('W-' + plain) || pick(plain);
		if (!chosen) continue;

		if (!Object.prototype.hasOwnProperty.call(overlay, key)) overlay[key] = chosen.value;

		// Also set the exact original casing, not just the lower-cased key: the base locale
		// JSON (de-DE.json etc.) defines some of these same keys with intentional casing (e.g.
		// "Account"), and flatMessageResolver()/$T() both check the exact-case key first — so
		// without this, a same-cased base entry would always shadow this overlay value.
		if (!seenExactCase.has(plain)) {
			seenExactCase.add(plain);
			overlay[plain] = chosen.value;
		}
	}

	return overlay;
}
